import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Download, Plus, Trash2, Upload, RefreshCw } from "lucide-react";
import { api, getToken, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const Field = ({ label, children }) => (
  <div className="mb-4">
    <label className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A55]">{label}</label>
    {children}
  </div>
);

const ImageUpload = ({ value, onChange, testId }) => {
  const ref = useRef();
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Obrázek je příliš velký (max 3 MB)"); return; }
    const r = new FileReader();
    r.onload = () => onChange(r.result);
    r.readAsDataURL(f);
  };
  return (
    <div className="flex items-center gap-3">
      {value ? (
        <img src={assetUrl(value)} alt="" className="h-16 w-24 rounded-md border border-[#E5E0D8] object-cover" />
      ) : (
        <div className="flex h-16 w-24 items-center justify-center rounded-md border border-dashed border-[#E5E0D8] text-xs text-[#5C5A55]">Bez obrázku</div>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
      <Button type="button" size="sm" variant="outline" data-testid={testId} onClick={() => ref.current.click()}>
        <Upload size={14} className="mr-1" /> Nahrát
      </Button>
      {value && (
        <Button type="button" size="sm" variant="outline" className="text-red-500" onClick={() => onChange("")}>
          <Trash2 size={14} />
        </Button>
      )}
    </div>
  );
};

export default function WorldEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [world, setWorld] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    if (!getToken()) { navigate("/admin"); return; }
    api.getWorld(id).then((r) => setWorld(r.data)).catch(() => toast.error("Svět nenalezen"));
  }, [id, navigate]);

  if (!world) return <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7] text-[#5C5A55]">Načítám…</div>;

  const set = (path, value) => {
    setWorld((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let o = next;
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
      o[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setList = (key, idx, field, value) => {
    setWorld((prev) => {
      const next = structuredClone(prev);
      next[key][idx][field] = value;
      return next;
    });
  };

  const addRow = (key, row) => setWorld((p) => ({ ...p, [key]: [...p[key], row] }));
  const delRow = (key, idx) => setWorld((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.updateWorld(world.id, world);
      toast.success("Uloženo – náhled aktualizován");
      setPreviewKey((k) => k + 1);
    } catch {
      toast.error("Uložení se nezdařilo");
    } finally {
      setSaving(false);
    }
  };

  const downloadZip = async () => {
    try {
      await api.updateWorld(world.id, world);
      setPreviewKey((k) => k + 1);
      const a = document.createElement("a");
      a.href = api.zipUrl(world.id);
      a.download = `svatebni-web-${world.slug}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("ZIP se generuje a stahuje");
    } catch {
      toast.error("Generování se nezdařilo");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="sticky top-0 z-40 flex items-center justify-between border-b border-[#E5E0D8] bg-white px-6 py-3">
        <Link to="/admin" className="flex items-center gap-2 text-sm text-[#5C5A55] hover:text-[#C5A059]" data-testid="editor-back-btn">
          <ArrowLeft size={16} /> Zpět na přehled
        </Link>
        <div className="font-heading text-lg">{world.name}</div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" data-testid="refresh-preview-btn" onClick={() => setPreviewKey((k) => k + 1)}>
            <RefreshCw size={14} />
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="save-world-btn" className="bg-[#1A1A1A] hover:bg-[#C5A059]">
            <Save size={14} className="mr-1" /> {saving ? "Ukládám…" : "Uložit"}
          </Button>
          <Button size="sm" onClick={downloadZip} data-testid="generate-zip-button" className="bg-[#C5A059] hover:bg-[#D4B473]">
            <Download size={14} className="mr-1" /> Vygenerovat ZIP
          </Button>
        </div>
      </nav>

      <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
        <div className="max-h-[calc(100vh-57px)] overflow-y-auto p-6 md:p-8" data-testid="world-editor-form">
          <Tabs defaultValue="zakladni">
            <TabsList className="mb-6 flex-wrap">
              <TabsTrigger value="zakladni" data-testid="tab-zakladni">Základní</TabsTrigger>
              <TabsTrigger value="sekce" data-testid="tab-sekce">Sekce</TabsTrigger>
              <TabsTrigger value="menu" data-testid="tab-menu">Menu</TabsTrigger>
              <TabsTrigger value="program" data-testid="tab-program">Program</TabsTrigger>
              <TabsTrigger value="zasedaci" data-testid="tab-zasedaci">Zasedací pořádek</TabsTrigger>
              <TabsTrigger value="vzhled" data-testid="tab-vzhled">Vzhled</TabsTrigger>
            </TabsList>

            <TabsContent value="zakladni">
              <Field label="Název světa"><Input data-testid="input-world-name" value={world.name} onChange={(e) => set("name", e.target.value)} /></Field>
              <Field label="Uvítací titulek (v grafice webu)"><Input data-testid="input-welcome-title" value={world.welcome_title} onChange={(e) => set("welcome_title", e.target.value)} /></Field>
              <Field label="Podtitulek / motto"><Input data-testid="input-tagline" value={world.tagline} onChange={(e) => set("tagline", e.target.value)} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Jméno 1"><Input data-testid="input-name1" value={world.couple.name1} onChange={(e) => set("couple.name1", e.target.value)} /></Field>
                <Field label="Jméno 2"><Input data-testid="input-name2" value={world.couple.name2} onChange={(e) => set("couple.name2", e.target.value)} /></Field>
              </div>
              <Field label="Iniciály (monogram v grafice)"><Input data-testid="input-initials" value={world.couple.initials} onChange={(e) => set("couple.initials", e.target.value)} /></Field>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Datum"><Input data-testid="input-date" value={world.wedding.date} onChange={(e) => set("wedding.date", e.target.value)} /></Field>
                <Field label="Čas"><Input data-testid="input-time" value={world.wedding.time} onChange={(e) => set("wedding.time", e.target.value)} /></Field>
                <Field label="Místo konání"><Input data-testid="input-venue" value={world.wedding.venue} onChange={(e) => set("wedding.venue", e.target.value)} /></Field>
              </div>
            </TabsContent>

            <TabsContent value="sekce">
              {world.sections.map((s, i) => (
                <div key={s.key} className="mb-6 rounded-xl border border-[#E5E0D8] bg-white p-5">
                  <div className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#C5A059]">Sekce {i + 1} · {s.type}</div>
                  <Field label="Název sekce"><Input data-testid={`input-section-title-${s.key}`} value={s.title} onChange={(e) => setList("sections", i, "title", e.target.value)} /></Field>
                  <Field label="Podtitulek"><Input value={s.subtitle} onChange={(e) => setList("sections", i, "subtitle", e.target.value)} /></Field>
                  <Field label="Text sekce"><Textarea rows={3} data-testid={`input-section-text-${s.key}`} value={s.text} onChange={(e) => setList("sections", i, "text", e.target.value)} /></Field>
                  <Field label="Obrázek sekce">
                    <ImageUpload value={s.image} testId={`upload-section-image-${s.key}`} onChange={(v) => setList("sections", i, "image", v)} />
                  </Field>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="menu">
              {world.menu.map((m, i) => (
                <div key={i} className="mb-3 flex items-end gap-3">
                  <div className="w-40"><Field label="Chod"><Input value={m.course} onChange={(e) => setList("menu", i, "course", e.target.value)} /></Field></div>
                  <div className="flex-1"><Field label="Jídlo"><Input data-testid={`input-menu-name-${i}`} value={m.name} onChange={(e) => setList("menu", i, "name", e.target.value)} /></Field></div>
                  <Button variant="outline" size="sm" className="mb-4 text-red-500" onClick={() => delRow("menu", i)}><Trash2 size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="add-menu-item-btn" onClick={() => addRow("menu", { course: "Chod", name: "" })}>
                <Plus size={14} className="mr-1" /> Přidat chod
              </Button>
            </TabsContent>

            <TabsContent value="program">
              {world.program.map((p, i) => (
                <div key={i} className="mb-3 flex items-end gap-3">
                  <div className="w-28"><Field label="Čas"><Input value={p.time} onChange={(e) => setList("program", i, "time", e.target.value)} /></Field></div>
                  <div className="flex-1"><Field label="Bod programu"><Input data-testid={`input-program-item-${i}`} value={p.item} onChange={(e) => setList("program", i, "item", e.target.value)} /></Field></div>
                  <Button variant="outline" size="sm" className="mb-4 text-red-500" onClick={() => delRow("program", i)}><Trash2 size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="add-program-item-btn" onClick={() => addRow("program", { time: "12:00", item: "" })}>
                <Plus size={14} className="mr-1" /> Přidat bod
              </Button>
            </TabsContent>

            <TabsContent value="zasedaci">
              {world.seating.map((t, i) => (
                <div key={i} className="mb-5 rounded-xl border border-[#E5E0D8] bg-white p-5">
                  <div className="mb-2 flex items-end gap-3">
                    <div className="flex-1"><Field label="Název stolu"><Input data-testid={`input-table-name-${i}`} value={t.table} onChange={(e) => setList("seating", i, "table", e.target.value)} /></Field></div>
                    <Button variant="outline" size="sm" className="mb-4 text-red-500" onClick={() => delRow("seating", i)}><Trash2 size={14} /></Button>
                  </div>
                  <Field label="Hosté (jeden na řádek)">
                    <Textarea rows={4} value={t.guests.join("\n")} onChange={(e) => setList("seating", i, "guests", e.target.value.split("\n"))} />
                  </Field>
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="add-table-btn" onClick={() => addRow("seating", { table: `Stůl ${world.seating.length + 1}`, guests: [] })}>
                <Plus size={14} className="mr-1" /> Přidat stůl
              </Button>
            </TabsContent>

            <TabsContent value="vzhled">
              <Field label="Hlavní obrázek (hero pozadí)">
                <ImageUpload value={world.hero_image} testId="upload-hero-image" onChange={(v) => set("hero_image", v)} />
              </Field>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {["bg", "surface", "accent", "text", "muted", "border"].map((k) => (
                  <Field key={k} label={`Barva – ${k}`}>
                    <div className="flex items-center gap-2">
                      <input type="color" value={world.theme[k]} onChange={(e) => set(`theme.${k}`, e.target.value)} className="h-9 w-12 cursor-pointer rounded border border-[#E5E0D8]" data-testid={`color-${k}`} />
                      <Input value={world.theme[k]} onChange={(e) => set(`theme.${k}`, e.target.value)} />
                    </div>
                  </Field>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Font nadpisů"><Input value={world.theme.heading_font} onChange={(e) => set("theme.heading_font", e.target.value)} /></Field>
                <Field label="Font textu"><Input value={world.theme.body_font} onChange={(e) => set("theme.body_font", e.target.value)} /></Field>
              </div>
              <Field label="Google Fonts parametr"><Input value={world.theme.google_fonts} onChange={(e) => set("theme.google_fonts", e.target.value)} /></Field>
              <Field label="Náhledový obrázek (karta na hlavním webu – URL)">
                <Input value={world.preview_image} onChange={(e) => set("preview_image", e.target.value)} />
              </Field>
            </TabsContent>
          </Tabs>
        </div>

        <div className="hidden border-l border-[#E5E0D8] bg-[#111] lg:block">
          <div className="flex items-center justify-between bg-[#1A1A1A] px-4 py-2 text-xs uppercase tracking-widest text-white/50">
            Živý náhled (uložte pro aktualizaci)
          </div>
          <iframe
            key={previewKey}
            title="Náhled"
            data-testid="editor-preview-iframe"
            src={api.previewUrl(world.id)}
            className="h-[calc(100vh-90px)] w-full border-0"
          />
        </div>
      </div>
    </div>
  );
}
